import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  HOLE_POOL,
  VW,
  VH,
  OBS_SIZE,
  applyChamfers,
} from '../../games/minigolf/Game';
import MinigolfGame from '../../games/minigolf/Game';
import { XMarkIcon, PlayIcon, ArrowPathIcon } from '@heroicons/react/24/solid';

// Editor for the built-in mini golf hole pool. Renders a top-down 2D preview
// of each hole (course polygon + chamfers + obstacles + ball + flag), lets the
// admin edit the simple data (ball / hole / obstacles / chamfer toggles /
// openEdges / name / par / tier) on top of the existing course shapes, and
// outputs a JS object snippet to paste back into Game.jsx. No persistence.

const TOOLS = {
  MOVE: 'move',          // drag ball/hole/obstacle to move; click vertex/edge to toggle; shift-click obstacle to delete
  ADD_OBSTACLE: 'add',   // next canvas click adds a 34×34 obstacle (drag still works on existing items)
};

// Pixel-distance threshold under which a mousedown→mouseup is treated as a
// click, not a drag. Lets click-to-toggle coexist with drag-to-move.
const DRAG_THRESHOLD = 4;
const BALL_HIT_R = 14;
const HOLE_HIT_R = 14;

// Snap freehand clicks to a 2px grid so coords stay clean in exported JS.
const SNAP = 2;
const snap = (n) => Math.round(n / SNAP) * SNAP;

// Deep clone hole data so edits never mutate the imported HOLE_POOL.
function cloneHole(h) {
  return JSON.parse(JSON.stringify(h));
}

// Format a single hole as a JS literal that can be pasted directly into
// HOLE_POOL. Multi-line, sorted keys, integer coords (we already snap).
function holeToJsLiteral(hole) {
  const fields = [];
  fields.push(`name: ${JSON.stringify(hole.name)}`);
  fields.push(`par: ${hole.par}`);
  fields.push(`ball: { x: ${hole.ball.x}, y: ${hole.ball.y} }`);
  fields.push(`hole: { x: ${hole.hole.x}, y: ${hole.hole.y} }`);
  if (hole.obstacles && hole.obstacles.length > 0) {
    const obs = hole.obstacles
      .map((o) => `{ x: ${o.x}, y: ${o.y}, w: ${o.w}, h: ${o.h} }`)
      .join(', ');
    fields.push(`obstacles: [${obs}]`);
  } else {
    fields.push(`obstacles: []`);
  }
  if (hole.openEdges && hole.openEdges.length > 0) {
    fields.push(`openEdges: [${hole.openEdges.join(', ')}]`);
  } else {
    fields.push(`openEdges: []`);
  }
  fields.push(`courseShape: ${JSON.stringify(hole.courseShape)}`);
  if (hole.chamfers && hole.chamfers.length > 0) {
    const ch = hole.chamfers
      .map((c) => {
        const parts = [];
        if (c.poly != null) parts.push(`poly: ${c.poly}`);
        parts.push(`vertex: ${c.vertex}`);
        parts.push(`size: ${c.size || 40}`);
        return `{ ${parts.join(', ')} }`;
      })
      .join(', ');
    fields.push(`chamfers: [${ch}]`);
  }
  return `{ ${fields.join(', ')} }`;
}

// Outer polygon (poly 0 for compound, the whole shape for simple)
function getOuterPoly(hole) {
  const s = hole.courseShape;
  if (!s) return [];
  return Array.isArray(s[0]) ? s[0] : s;
}

// True when courseShape is a compound (array of polygons)
function isCompound(hole) {
  return Array.isArray(hole.courseShape?.[0]);
}

// Render the editor preview into a 2D canvas. Mirrors the game's data flow
// (applyChamfers → polygon → obstacles → ball → flag) without the 3D shadow.
function renderHole(ctx, hole, opts = {}) {
  const { selectedTool = TOOLS.MOVE, hoveredVertex = null, hoveredEdge = null } = opts;
  ctx.clearRect(0, 0, VW, VH);

  // Background — soft water/sky
  ctx.fillStyle = '#7fb3ff';
  ctx.fillRect(0, 0, VW, VH);

  // Resolve final shape after chamfers
  const resolved = applyChamfers(hole);
  const polys = isCompound(resolved)
    ? resolved.courseShape
    : [resolved.courseShape];

  // Fill course shape (grass)
  ctx.fillStyle = '#5cb85c';
  ctx.beginPath();
  polys.forEach((poly) => {
    poly.forEach((v, i) => {
      if (i === 0) ctx.moveTo(v.x, v.y);
      else ctx.lineTo(v.x, v.y);
    });
    ctx.closePath();
  });
  ctx.fill();

  // Stroke course outline
  ctx.lineWidth = 2;
  ctx.strokeStyle = '#2a7a2a';
  polys.forEach((poly) => {
    ctx.beginPath();
    poly.forEach((v, i) => {
      if (i === 0) ctx.moveTo(v.x, v.y);
      else ctx.lineTo(v.x, v.y);
    });
    ctx.closePath();
    ctx.stroke();
  });

  // Highlight open edges (red dashed)
  const outerResolved = isCompound(resolved) ? resolved.courseShape[0] : resolved.courseShape;
  if (hole.openEdges && hole.openEdges.length > 0) {
    ctx.save();
    ctx.lineWidth = 3;
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = '#e53935';
    hole.openEdges.forEach((edgeIdx) => {
      const a = outerResolved[edgeIdx];
      const b = outerResolved[(edgeIdx + 1) % outerResolved.length];
      if (!a || !b) return;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    });
    ctx.restore();
  }

  // Obstacles
  hole.obstacles?.forEach((o) => {
    ctx.fillStyle = '#3e2a1f';
    ctx.fillRect(o.x, o.y, o.w, o.h);
    ctx.strokeStyle = '#1f140b';
    ctx.lineWidth = 1;
    ctx.strokeRect(o.x + 0.5, o.y + 0.5, o.w - 1, o.h - 1);
  });

  // Flag (hole position)
  if (hole.hole) {
    ctx.beginPath();
    ctx.fillStyle = '#000';
    ctx.arc(hole.hole.x, hole.hole.y, 8, 0, Math.PI * 2);
    ctx.fill();
    // Flag pole
    ctx.strokeStyle = '#222';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(hole.hole.x, hole.hole.y);
    ctx.lineTo(hole.hole.x, hole.hole.y - 28);
    ctx.stroke();
    // Flag
    ctx.fillStyle = '#e53935';
    ctx.beginPath();
    ctx.moveTo(hole.hole.x, hole.hole.y - 28);
    ctx.lineTo(hole.hole.x + 14, hole.hole.y - 24);
    ctx.lineTo(hole.hole.x, hole.hole.y - 20);
    ctx.closePath();
    ctx.fill();
  }

  // Ball
  if (hole.ball) {
    ctx.beginPath();
    ctx.fillStyle = '#fff';
    ctx.arc(hole.ball.x, hole.ball.y, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#888';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // Vertex dots on the AUTHORED (pre-chamfer) outer polygon. Clicking these
  // toggles a chamfer at that vertex. Hovered vertex is bigger / golden.
  const authoredOuter = getOuterPoly(hole);
  const chamferedVerts = new Set((hole.chamfers || [])
    .filter((c) => (c.poly == null || c.poly === 0))
    .map((c) => c.vertex));
  authoredOuter.forEach((v, i) => {
    const isChamfered = chamferedVerts.has(i);
    const isHovered = hoveredVertex === i;
    ctx.beginPath();
    ctx.arc(v.x, v.y, isHovered ? 9 : 6, 0, Math.PI * 2);
    ctx.fillStyle = isChamfered ? '#FFD700' : 'rgba(255,255,255,0.85)';
    ctx.fill();
    ctx.strokeStyle = isHovered ? '#000' : '#222';
    ctx.lineWidth = 2;
    ctx.stroke();
  });

  // Edge midpoint dots — clicking toggles openEdges for that index
  for (let i = 0; i < authoredOuter.length; i++) {
    const a = authoredOuter[i];
    const b = authoredOuter[(i + 1) % authoredOuter.length];
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const isOpen = hole.openEdges?.includes(i);
    const isHov = hoveredEdge === i;
    ctx.beginPath();
    ctx.arc(mx, my, isHov ? 7 : 5, 0, Math.PI * 2);
    ctx.fillStyle = isOpen ? '#e53935' : 'rgba(255,255,255,0.6)';
    ctx.fill();
    ctx.strokeStyle = '#222';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  // Cursor crosshair preview when in a place-something tool — drawn last.
  if (selectedTool === TOOLS.ADD_OBSTACLE && opts.cursorPos) {
    const { x, y } = opts.cursorPos;
    ctx.save();
    ctx.globalAlpha = 0.4;
    ctx.fillStyle = '#3e2a1f';
    ctx.fillRect(x - OBS_SIZE / 2, y - OBS_SIZE / 2, OBS_SIZE, OBS_SIZE);
    ctx.restore();
  }
}

// Find the closest authored outer vertex within `radius` of (x, y). Returns
// the vertex index or null.
function pickVertex(hole, x, y, radius = 14) {
  const poly = getOuterPoly(hole);
  let best = null;
  let bestDist = radius * radius;
  for (let i = 0; i < poly.length; i++) {
    const dx = poly[i].x - x;
    const dy = poly[i].y - y;
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

// Find the edge whose midpoint is closest to (x, y) within radius.
function pickEdge(hole, x, y, radius = 12) {
  const poly = getOuterPoly(hole);
  let best = null;
  let bestDist = radius * radius;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const dx = mx - x;
    const dy = my - y;
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

// Move one edge of the outer polygon along its perpendicular axis. Both
// endpoints of the edge slide on the same axis (y for horizontal edges,
// x for vertical) so the edge keeps its orientation and the polygon stays
// rectilinear. Adjacent walls naturally extend or shrink to follow.
function moveOuterEdge(hole, i0, i1, isHorizontal, newAxisValue) {
  const replace = (poly) => {
    const next = poly.map((v) => ({ ...v }));
    if (isHorizontal) {
      next[i0].y = newAxisValue;
      next[i1].y = newAxisValue;
    } else {
      next[i0].x = newAxisValue;
      next[i1].x = newAxisValue;
    }
    return next;
  };
  if (isCompound(hole)) {
    const nextShape = hole.courseShape.map((p, i) => (i === 0 ? replace(p) : p.map((v) => ({ ...v }))));
    return { ...hole, courseShape: nextShape };
  }
  return { ...hole, courseShape: replace(hole.courseShape) };
}

// Hit-test obstacles (returns index) — used for click-to-delete
function pickObstacle(hole, x, y) {
  const list = hole.obstacles || [];
  for (let i = list.length - 1; i >= 0; i--) {
    const o = list[i];
    if (x >= o.x && x <= o.x + o.w && y >= o.y && y <= o.y + o.h) return i;
  }
  return -1;
}

export default function AdminMiniGolfHoles() {
  const [tier, setTier] = useState('easy');
  const [index, setIndex] = useState(0);
  // All in-progress edits across every hole, keyed by `${tier}:${index}`.
  // The visible hole is derived from `drafts[currentKey] || HOLE_POOL[...]`.
  // Switching selection no longer discards edits; they're held in this map.
  const [drafts, setDrafts] = useState({});
  const [tool, setTool] = useState(TOOLS.MOVE);
  const [hover, setHover] = useState({ vertex: null, edge: null, draggable: null });
  const [cursor, setCursor] = useState(null);
  const [copyMsg, setCopyMsg] = useState('');
  const canvasRef = useRef(null);
  const canvasWrapRef = useRef(null);
  // Drag state lives in a ref (not React state) so the rAF-style mousemove
  // loop doesn't trigger excess re-renders. Setting drag.hasMoved is the
  // only state we care about externally and it's only read at mouseup.
  const dragRef = useRef(null);
  // Preview state — `previewHole` is a snapshot taken when Play is clicked
  // so live edits don't disturb the running game. `previewKey` bumps to
  // remount the Game (cheap re-init).
  const [previewHole, setPreviewHole] = useState(null);
  const [previewKey, setPreviewKey] = useState(0);

  // Minimal gameData stub for the Game canvas — only fields the engine reads.
  const previewGameData = useMemo(() => ({
    id: 'admin-preview',
    gameType: 'minigolf',
    options: {
      customColors: {
        background: '#7fb3ff',
        primary: '#5cb85c',
        primaryAction: '#FFD700',
        secondaryAction: '#EF4444',
        buttonText: '#FFFFFF',
        text: '#1F2937',
        surface: '#FFFFFF',
      },
      holeCount: 1,
      difficulty: 'mixed',
    },
  }), []);

  // Current selection key + the visible hole (draft if any, else source).
  // `setHole` always writes into the draft for the current key — that way
  // switching selection never clobbers in-progress edits.
  const currentKey = `${tier}:${index}`;
  const sourceHole = HOLE_POOL[tier]?.[index];
  const hole = drafts[currentKey] || sourceHole;
  const setHole = (updater) => {
    setDrafts((d) => {
      const base = d[currentKey] || cloneHole(sourceHole);
      const next = typeof updater === 'function' ? updater(base) : updater;
      return { ...d, [currentKey]: next };
    });
  };

  // Redraw whenever anything that affects the visual changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    renderHole(ctx, hole, {
      selectedTool: tool,
      hoveredVertex: hover.vertex,
      hoveredEdge: hover.edge,
      cursorPos: cursor,
    });
  }, [hole, tool, hover, cursor]);

  const list = HOLE_POOL[tier] || [];

  // Convert page coords (the canvas might be CSS-scaled) → virtual coords.
  // Pass `snap: false` to skip 2px snapping (used for hit-testing where we
  // want raw mouse precision; positions are snapped only when committed).
  const toVirtual = (clientX, clientY, { snap: doSnap = true } = {}) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * VW;
    const y = ((clientY - rect.top) / rect.height) * VH;
    return doSnap ? { x: snap(x), y: snap(y) } : { x, y };
  };

  // What's under the cursor? Priority: ball → hole → obstacle → vertex → edge.
  // Used both for hover styling and to start a drag on mousedown.
  const hitTest = (hole, x, y) => {
    if (hole.ball) {
      const dx = hole.ball.x - x, dy = hole.ball.y - y;
      if (dx * dx + dy * dy <= BALL_HIT_R * BALL_HIT_R) return { kind: 'ball' };
    }
    if (hole.hole) {
      const dx = hole.hole.x - x, dy = hole.hole.y - y;
      if (dx * dx + dy * dy <= HOLE_HIT_R * HOLE_HIT_R) return { kind: 'hole' };
    }
    const oi = pickObstacle(hole, x, y);
    if (oi >= 0) return { kind: 'obstacle', index: oi };
    const v = pickVertex(hole, x, y);
    if (v != null) return { kind: 'vertex', index: v };
    const ed = pickEdge(hole, x, y);
    if (ed != null) return { kind: 'edge', index: ed };
    return null;
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // left click only
    const { x, y } = toVirtual(e.clientX, e.clientY, { snap: false });
    const hit = hitTest(hole, x, y);

    // Shift+click obstacle → delete immediately (since plain click now starts a drag)
    if (hit?.kind === 'obstacle' && e.shiftKey) {
      setHole((h) => ({ ...h, obstacles: h.obstacles.filter((_, i) => i !== hit.index) }));
      return;
    }

    // Draggable items: record an offset so dragging keeps grab-point under cursor
    if (hit?.kind === 'ball') {
      dragRef.current = { kind: 'ball', startX: x, startY: y, hasMoved: false, ox: hole.ball.x - x, oy: hole.ball.y - y };
      return;
    }
    if (hit?.kind === 'hole') {
      dragRef.current = { kind: 'hole', startX: x, startY: y, hasMoved: false, ox: hole.hole.x - x, oy: hole.hole.y - y };
      return;
    }
    if (hit?.kind === 'obstacle') {
      const o = hole.obstacles[hit.index];
      dragRef.current = { kind: 'obstacle', index: hit.index, startX: x, startY: y, hasMoved: false, ox: o.x - x, oy: o.y - y };
      return;
    }
    if (hit?.kind === 'edge') {
      // Edges are draggable but constrained to perpendicular motion so the
      // course stays rectilinear. We classify the edge here and record only
      // the relevant axis; mousemove updates only that axis on both endpoints.
      const poly = getOuterPoly(hole);
      const a = poly[hit.index];
      const b = poly[(hit.index + 1) % poly.length];
      const isHorizontal = Math.abs(a.y - b.y) < Math.abs(a.x - b.x);
      // Original axis value of the edge — used to compute per-frame delta
      const axisOrigin = isHorizontal ? a.y : a.x;
      dragRef.current = {
        kind: 'edge',
        index: hit.index,
        startX: x,
        startY: y,
        hasMoved: false,
        isHorizontal,
        axisOrigin,
        // Endpoint indices in the outer polygon — needed because chamfered
        // courses can have these indices be non-adjacent in the rendered shape,
        // but the AUTHORED outer poly we mutate is always [i, (i+1) % n].
        i0: hit.index,
        i1: (hit.index + 1) % poly.length,
      };
      return;
    }

    // Non-draggable hits: queue them up to fire on mouseup (treating as a click)
    // and bail. Don't start a drag — vertex/edge toggles are click-only.
    dragRef.current = { kind: 'click', startX: x, startY: y, hit, addModeAt: tool === TOOLS.ADD_OBSTACLE ? { x, y } : null };
  };

  const handleMouseMove = (e) => {
    const { x, y } = toVirtual(e.clientX, e.clientY, { snap: false });
    setCursor({ x: snap(x), y: snap(y) });

    const drag = dragRef.current;
    if (drag && drag.kind !== 'click') {
      const dx = x - drag.startX;
      const dy = y - drag.startY;
      if (!drag.hasMoved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
        drag.hasMoved = true;
      }
      if (drag.hasMoved) {
        const nx = snap(x + drag.ox);
        const ny = snap(y + drag.oy);
        if (drag.kind === 'ball') {
          setHole((h) => ({ ...h, ball: { x: nx, y: ny } }));
        } else if (drag.kind === 'hole') {
          setHole((h) => ({ ...h, hole: { x: nx, y: ny } }));
        } else if (drag.kind === 'obstacle') {
          setHole((h) => ({
            ...h,
            obstacles: h.obstacles.map((o, i) => i === drag.index ? { ...o, x: nx, y: ny } : o),
          }));
        } else if (drag.kind === 'edge') {
          // Perpendicular-only motion: move both endpoints by the same delta
          // on the cross-axis. The on-axis coords stay where they are, so the
          // edge keeps its orientation and the adjacent walls just extend or
          // shrink to follow — rectilinear shape preserved.
          const cursorAxis = drag.isHorizontal ? y : x;
          const newAxis = snap(cursorAxis + (drag.axisOrigin - (drag.isHorizontal ? drag.startY : drag.startX)));
          setHole((h) => moveOuterEdge(h, drag.i0, drag.i1, drag.isHorizontal, newAxis));
        }
      }
    }

    // Hover-feedback for cursor styling
    const hit = hitTest(hole, x, y);
    setHover({
      vertex: hit?.kind === 'vertex' ? hit.index : null,
      edge: hit?.kind === 'edge' ? hit.index : null,
      draggable: hit?.kind === 'ball' || hit?.kind === 'hole' || hit?.kind === 'obstacle' ? hit.kind : null,
    });
  };

  const handleMouseUp = (e) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;

    // Real drag occurred → commit handled in mousemove, nothing more to do
    if (drag.hasMoved) return;

    // Otherwise treat as a click: re-derive hit at the up position so a
    // small wobble doesn't matter, then run the appropriate toggle.
    const { x, y } = toVirtual(e.clientX, e.clientY, { snap: false });

    // ADD_OBSTACLE: clicked empty space → drop a new block centred on click
    if (tool === TOOLS.ADD_OBSTACLE) {
      const hit = hitTest(hole, x, y);
      // Don't drop a block on top of something else — that's confusing
      if (!hit || hit.kind === 'vertex' || hit.kind === 'edge') {
        const ox = snap(x - OBS_SIZE / 2);
        const oy = snap(y - OBS_SIZE / 2);
        setHole((h) => ({
          ...h,
          obstacles: [...(h.obstacles || []), { x: ox, y: oy, w: OBS_SIZE, h: OBS_SIZE }],
        }));
        return;
      }
    }

    // Toggle chamfer on vertex click
    const v = pickVertex(hole, x, y);
    if (v != null) {
      setHole((h) => {
        const chamfered = (h.chamfers || []).some((c) => (c.poly == null || c.poly === 0) && c.vertex === v);
        const next = (h.chamfers || []).filter((c) => !((c.poly == null || c.poly === 0) && c.vertex === v));
        if (!chamfered) {
          next.push(isCompound(h) ? { poly: 0, vertex: v, size: 40 } : { vertex: v, size: 40 });
        }
        return { ...h, chamfers: next };
      });
      return;
    }
    // Toggle open edge on edge midpoint click
    const ed = pickEdge(hole, x, y);
    if (ed != null) {
      setHole((h) => {
        const open = new Set(h.openEdges || []);
        if (open.has(ed)) open.delete(ed); else open.add(ed);
        return { ...h, openEdges: Array.from(open).sort((a, b) => a - b) };
      });
    }
  };

  const handleMouseLeave = () => {
    setCursor(null);
    setHover({ vertex: null, edge: null, draggable: null });
    dragRef.current = null;
  };

  const resetToSource = () => {
    setDrafts((d) => {
      if (!d[currentKey]) return d;
      const next = { ...d };
      delete next[currentKey];
      return next;
    });
  };

  const resetAllDrafts = () => {
    if (Object.keys(drafts).length === 0) return;
    const ok = window.confirm(`Discard edits on ${Object.keys(drafts).length} hole${Object.keys(drafts).length === 1 ? '' : 's'}?`);
    if (ok) setDrafts({});
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMsg(`${label} copied`);
      setTimeout(() => setCopyMsg(''), 2200);
    } catch (e) {
      setCopyMsg('Copy failed — open dev tools and copy from console');
      // eslint-disable-next-line no-console
      console.log(text);
    }
  };

  const copyThisHole = () => copyToClipboard(holeToJsLiteral(hole), 'Hole');

  // Snapshot the current hole into preview state and bump key so the Game
  // mounts fresh. Closing the modal stops the game by unmounting it.
  const handlePlay = () => {
    setPreviewHole(cloneHole(hole));
    setPreviewKey((k) => k + 1);
  };
  const handleReplay = () => setPreviewKey((k) => k + 1);
  const handleClosePreview = () => setPreviewHole(null);

  const copyAllHoles = () => {
    // Substitute every drafted hole into a copy of HOLE_POOL.
    const merge = (tierName) =>
      HOLE_POOL[tierName].map((h, i) => drafts[`${tierName}:${i}`] || h);
    const merged = {
      easy: merge('easy'),
      medium: merge('medium'),
      hard: merge('hard'),
    };
    const fmtTier = (arr) =>
      arr.map((h) => `    ${holeToJsLiteral(h)},`).join('\n');
    const text =
      `export const HOLE_POOL = {\n` +
      `  easy: [\n${fmtTier(merged.easy)}\n  ],\n` +
      `  medium: [\n${fmtTier(merged.medium)}\n  ],\n` +
      `  hard: [\n${fmtTier(merged.hard)}\n  ],\n` +
      `};\n`;
    copyToClipboard(text, 'Full HOLE_POOL');
  };

  // Coords of the authored outer vertices, used to render the chamfer toggle list
  const authoredOuter = getOuterPoly(hole);
  const chamferedSet = new Set((hole.chamfers || [])
    .filter((c) => (c.poly == null || c.poly === 0))
    .map((c) => c.vertex));
  const openEdgeSet = new Set(hole.openEdges || []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-white">Mini Golf Hole Editor</h2>
          <p className="text-xs gch-text-secondary mt-1">
            Edit the shared hole pool that ships in every mini-golf campaign. Changes are not saved — copy the JS output and paste it into <code className="text-[11px] px-1 py-0.5 rounded bg-white/10">src/games/minigolf/Game.jsx</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {copyMsg && (
            <span className="text-xs font-medium px-2 py-1 rounded bg-emerald-500/20 text-emerald-300">{copyMsg}</span>
          )}
          {Object.keys(drafts).length > 0 && (
            <span className="text-[11px] font-medium gch-text-secondary mr-1">
              {Object.keys(drafts).length} edited
            </span>
          )}
          <button
            onClick={resetToSource}
            disabled={!drafts[currentKey]}
            className="btn-admin-secondary btn-admin-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Reset hole
          </button>
          <button
            onClick={resetAllDrafts}
            disabled={Object.keys(drafts).length === 0}
            className="btn-admin-secondary btn-admin-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Reset all
          </button>
          <button onClick={handlePlay} className="btn-admin-secondary btn-admin-sm inline-flex items-center gap-1.5">
            <PlayIcon className="w-4 h-4" />
            Play this hole
          </button>
          <button onClick={copyThisHole} className="btn-admin-secondary btn-admin-sm">Copy hole JS</button>
          <button onClick={copyAllHoles} className="btn-admin-primary btn-admin-sm">Copy full HOLE_POOL</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_300px] gap-4">
        {/* Hole list */}
        <aside className="gch-card p-3 space-y-3 max-h-[80vh] overflow-y-auto">
          <div className="flex gap-1">
            {['easy', 'medium', 'hard'].map((t) => (
              <button
                key={t}
                onClick={() => { setTier(t); setIndex(0); }}
                className={`flex-1 px-2 py-1.5 rounded text-xs font-semibold capitalize transition-colors ${
                  tier === t ? 'bg-white/15 text-white' : 'gch-text-secondary hover:bg-white/5'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <ul className="space-y-1">
            {list.map((h, i) => {
              const isEdited = !!drafts[`${tier}:${i}`];
              const draftedName = drafts[`${tier}:${i}`]?.name;
              return (
                <li key={i}>
                  <button
                    onClick={() => setIndex(i)}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center gap-2 ${
                      index === i
                        ? 'bg-white/15 text-white'
                        : 'gch-text-secondary hover:bg-white/5'
                    }`}
                  >
                    <span className="font-mono opacity-50">{String(i).padStart(2, '0')}</span>
                    <span className="flex-1 truncate">
                      {draftedName || h.name} <span className="opacity-50">· par {h.par}</span>
                    </span>
                    {isEdited && (
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0"
                        title="Edited (unsaved)"
                        aria-label="Edited"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Canvas */}
        <div ref={canvasWrapRef} className="gch-card p-3 flex items-start justify-center">
          <div className="relative" style={{ width: 400, maxWidth: '100%' }}>
            <canvas
              ref={canvasRef}
              width={VW}
              height={VH}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              className="block w-full h-auto rounded ring-1 ring-white/10 select-none"
              style={{
                cursor: (() => {
                  if (tool === TOOLS.ADD_OBSTACLE) return 'crosshair';
                  if (hover.draggable) return 'grab';
                  if (hover.edge != null) {
                    // Show the resize cursor in the direction the edge actually
                    // moves so the affordance reads correctly.
                    const poly = getOuterPoly(hole);
                    const a = poly[hover.edge];
                    const b = poly[(hover.edge + 1) % poly.length];
                    const isHorizontal = Math.abs(a.y - b.y) < Math.abs(a.x - b.x);
                    return isHorizontal ? 'ns-resize' : 'ew-resize';
                  }
                  if (hover.vertex != null) return 'pointer';   // click-only — toggles chamfer
                  return 'default';
                })(),
              }}
            />
            <div className="absolute top-2 left-2 right-2 flex items-center gap-1 flex-wrap">
              {[
                { id: TOOLS.MOVE, label: 'Edit' },
                { id: TOOLS.ADD_OBSTACLE, label: '+ Obstacle' },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTool(t.id)}
                  className={`px-2 py-1 rounded text-[11px] font-semibold backdrop-blur transition-colors ${
                    tool === t.id ? 'bg-white text-black' : 'bg-black/60 text-white hover:bg-black/80'
                  }`}
                >
                  {t.label}
                </button>
              ))}
              {cursor && (
                <span className="ml-auto px-2 py-1 rounded text-[10px] font-mono bg-black/60 text-white/80 backdrop-blur">
                  {cursor.x}, {cursor.y}
                </span>
              )}
            </div>
            <div className="mt-2 text-[11px] gch-text-secondary leading-relaxed">
              <b>Drag</b> the ball, flag, or obstacles to move them.{' '}
              <b>Drag a wall</b> by its edge midpoint dot — it slides perpendicular and keeps the course rectilinear.{' '}
              <b>Click</b> a corner dot to toggle a 45° chamfer.{' '}
              <b>Click</b> an edge midpoint (without dragging) to toggle fall-off.{' '}
              <b>Shift+click</b> an obstacle to delete it.
            </div>
          </div>
        </div>

        {/* Properties */}
        <aside className="gch-card p-3 space-y-3 max-h-[80vh] overflow-y-auto">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold gch-text-secondary mb-1">Name</label>
            <input
              type="text"
              value={hole.name || ''}
              onChange={(e) => setHole((h) => ({ ...h, name: e.target.value }))}
              className="w-full px-2 py-1.5 rounded bg-white/5 text-white text-sm ring-1 ring-white/10 focus:ring-white/30 outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] uppercase tracking-wider font-semibold gch-text-secondary mb-1">Par</label>
              <select
                value={hole.par}
                onChange={(e) => setHole((h) => ({ ...h, par: Number(e.target.value) }))}
                className="w-full px-2 py-1.5 rounded bg-white/5 text-white text-sm ring-1 ring-white/10 outline-none"
              >
                {[2, 3, 4].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider font-semibold gch-text-secondary mb-1">Tier</label>
              <div className="text-sm gch-text-secondary px-2 py-1.5 rounded bg-white/5 ring-1 ring-white/10 capitalize">{tier}</div>
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold gch-text-secondary mb-1">Ball</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={hole.ball?.x ?? 0} onChange={(e) => setHole((h) => ({ ...h, ball: { ...h.ball, x: snap(Number(e.target.value)) } }))} className="px-2 py-1 rounded bg-white/5 text-white text-sm ring-1 ring-white/10 outline-none" />
              <input type="number" value={hole.ball?.y ?? 0} onChange={(e) => setHole((h) => ({ ...h, ball: { ...h.ball, y: snap(Number(e.target.value)) } }))} className="px-2 py-1 rounded bg-white/5 text-white text-sm ring-1 ring-white/10 outline-none" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold gch-text-secondary mb-1">Hole</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={hole.hole?.x ?? 0} onChange={(e) => setHole((h) => ({ ...h, hole: { ...h.hole, x: snap(Number(e.target.value)) } }))} className="px-2 py-1 rounded bg-white/5 text-white text-sm ring-1 ring-white/10 outline-none" />
              <input type="number" value={hole.hole?.y ?? 0} onChange={(e) => setHole((h) => ({ ...h, hole: { ...h.hole, y: snap(Number(e.target.value)) } }))} className="px-2 py-1 rounded bg-white/5 text-white text-sm ring-1 ring-white/10 outline-none" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold gch-text-secondary mb-2">Chamfers (45° corners)</label>
            <div className="grid grid-cols-4 gap-1">
              {authoredOuter.map((v, i) => {
                const isOn = chamferedSet.has(i);
                return (
                  <button
                    key={i}
                    onClick={() => setHole((h) => {
                      const filtered = (h.chamfers || []).filter((c) => !((c.poly == null || c.poly === 0) && c.vertex === i));
                      if (isOn) return { ...h, chamfers: filtered };
                      filtered.push(isCompound(h) ? { poly: 0, vertex: i, size: 40 } : { vertex: i, size: 40 });
                      return { ...h, chamfers: filtered };
                    })}
                    className={`px-1.5 py-1 rounded text-[11px] font-mono transition-colors ${
                      isOn ? 'bg-yellow-400 text-black font-bold' : 'bg-white/5 text-white/70 hover:bg-white/10'
                    }`}
                    title={`Vertex ${i} at (${v.x}, ${v.y})`}
                  >
                    v{i}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold gch-text-secondary mb-2">Open edges</label>
            <div className="grid grid-cols-4 gap-1">
              {authoredOuter.map((_, i) => {
                const isOn = openEdgeSet.has(i);
                return (
                  <button
                    key={i}
                    onClick={() => setHole((h) => {
                      const open = new Set(h.openEdges || []);
                      if (open.has(i)) open.delete(i); else open.add(i);
                      return { ...h, openEdges: Array.from(open).sort((a, b) => a - b) };
                    })}
                    className={`px-1.5 py-1 rounded text-[11px] font-mono transition-colors ${
                      isOn ? 'bg-red-500 text-white font-bold' : 'bg-white/5 text-white/70 hover:bg-white/10'
                    }`}
                    title={`Edge ${i} (from vertex ${i} to vertex ${(i + 1) % authoredOuter.length})`}
                  >
                    e{i}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold gch-text-secondary mb-2">
              Obstacles ({hole.obstacles?.length || 0})
            </label>
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {(hole.obstacles || []).map((o, i) => (
                <li key={i} className="flex items-center justify-between text-[11px] font-mono px-2 py-1 rounded bg-white/5">
                  <span className="text-white/70">{`(${o.x}, ${o.y}) ${o.w}×${o.h}`}</span>
                  <button
                    onClick={() => setHole((h) => ({ ...h, obstacles: h.obstacles.filter((_, j) => j !== i) }))}
                    className="text-red-400 hover:text-red-300"
                    aria-label="Remove obstacle"
                  >
                    ✕
                  </button>
                </li>
              ))}
              {(hole.obstacles || []).length === 0 && (
                <li className="text-[11px] gch-text-secondary px-2 py-1">No obstacles</li>
              )}
            </ul>
          </div>

          <details className="text-xs">
            <summary className="cursor-pointer gch-text-secondary py-1">JS preview</summary>
            <pre className="mt-2 p-2 rounded bg-black/40 text-[10px] text-white/80 whitespace-pre-wrap break-all max-h-48 overflow-auto">
              {holeToJsLiteral(hole)}
            </pre>
          </details>
        </aside>
      </div>

      {/* Test-play modal — mounts a fresh Game with the snapshot hole as the
          only entry. Closes by unmounting; Replay re-keys the Game. */}
      {previewHole && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) handleClosePreview(); }}
        >
          <div
            className="relative bg-black rounded-2xl ring-1 ring-white/15 shadow-2xl overflow-hidden"
            style={{ width: 'min(400px, calc(100vw - 32px))', aspectRatio: `${VW} / ${VH}` }}
          >
            <MinigolfGame
              key={previewKey}
              game={previewGameData}
              onComplete={() => { /* leave the canvas mounted; user can Replay or Close */ }}
              onGameplayStart={() => {}}
              onScoreChange={() => {}}
              settings={{
                ...previewGameData.options,
                customHoles: [previewHole],
                holeCount: 1,
              }}
            />
            <div className="absolute top-2 right-2 flex items-center gap-1.5 z-10">
              <button
                onClick={handleReplay}
                title="Replay this hole"
                aria-label="Replay this hole"
                className="p-2 rounded-lg bg-white/15 hover:bg-white/25 text-white backdrop-blur transition-colors"
              >
                <ArrowPathIcon className="w-4 h-4" />
              </button>
              <button
                onClick={handleClosePreview}
                title="Close"
                aria-label="Close test play"
                className="p-2 rounded-lg bg-white/15 hover:bg-white/25 text-white backdrop-blur transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="absolute bottom-2 left-2 right-2 flex items-center justify-center pointer-events-none">
              <span className="px-2 py-1 rounded text-[10px] font-mono bg-black/60 text-white/70 backdrop-blur">
                Drag to aim · release to putt
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
